import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import store from '@/store'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'app',
    redirect: '/zh-hant/login',
  },
  {
    path: '/:lang/login',
    name: 'login',
    component: () =>
      import(/* webpackChunkName: "login" */ '@/views/TheLogin.vue'),
    children: [
      { path: '/:patchMatch(.*)*', redirect: '/zh-hant/login' }
    ]
  },
  {
    path: '/:lang/forget-password',
    name: 'forget_password',
    component: () =>
      import(/* webpackChunkName: "password" */ '@/views/Password/ForgetPassword.vue'),
  },
  {
    path: '/:lang/:memberId/reset-password/:resetPasswordToken',
    name: 'reset_password',
    component: () =>
      import(/* webpackChunkName: "password" */ '@/views/Password/ResetPassword.vue'),
  },
  {
    path: '/:lang/reset-password/result/:statusCode',
    name: 'reset_password_result',
    component: () =>
      import(/* webpackChunkName: "password" */ '@/views/Password/ResetPasswordResult.vue'),
  },
  {
    path: '/:lang/subscription',
    name: 'subscription',
    component: () =>
      import(/* webpackChunkName: "subscription" */ '@/views/Subscription/PlanSubscription.vue'),
  },
  {
    path: '/:lang/subscription/payment/result',
    name: 'payment_result',
    component: () =>
      import(/* webpackChunkName: "subscription" */ '@/views/Subscription/PlanPaymentResult.vue'),
  },
  {
    path: '/:lang/register',
    name: 'register',
    component: () =>
      import(/* webpackChunkName: "register" */ '@/views/Register/TheRegister.vue'),
  },
  {
    path: '/:lang/register/result/:statusCode',
    name: 'register_result',
    component: () =>
      import(/* webpackChunkName: "register" */ '@/views/Register/RegisterResult.vue'),
  },
  {
    path: '/:lang/:memberId/confirm-email/:confirmationToken',
    name: 'confirm_email',
    component: () =>
      import(/* webpackChunkName: "register" */ '@/views/Register/EmailConfirmResult.vue'),
  },
  {
    path: '/:lang/member_check',
    name: 'member_check',
    component: () =>
      import(/* webpackChunkName: "main" */ '@/views/MemberCheck/MemberCheck.vue'),
  },
  {
    path: '/:lang',
    name: 'main',
    component: () =>
      import(/* webpackChunkName: "main" */ '@/views/TheMain.vue'),
    children: [
      {
        path: 'migration_member_check',
        name: 'migration_member_check',
        component: () =>
          import(/* webpackChunkName: "main" */ '@/views/MemberCheck/MigrationMemberCheck.vue'),
      },
      {
        path: 'home',
        name: 'home',
        component: () =>
          import(/* webpackChunkName: "main" */ '@/views/TheHome.vue'),
      },
      /* TODO: CYBSS-480 CYBSS-482
      {
        path: 'member_profile',
        name: 'member_profile',
        component: () =>
          import(/!* webpackChunkName: "member" *!/ '@/views/MemberProfile.vue'),
      },
      */
      {
        path: 'subscription_management',
        name: 'subscription_management',
        component: () =>
          import(/* webpackChunkName: "subscription" */ '@/views/MySubscription.vue'),
      },
      {
        path: 'gup_statistics_overview',
        name: 'gup_statistics_overview',
        component: () =>
          import(/* webpackChunkName: "overview" */ '@/views/GupStatisticsOverview.vue'),
      },
      {
        path: 'point_issue_analysis',
        name: 'point_issue_analysis',
        component: () =>
          import(/* webpackChunkName: "IssueAnalysis" */ '@/views/PointIssueAnalysis.vue'),
      },
      {
        path: 'point_redeem_analysis',
        name: 'point_redeem_analysis',
        component: () =>
          import(/* webpackChunkName: "RedeemAnalysis" */ '@/views/PointRedeemAnalysis.vue'),
      },
      {
        path: 'point_record',
        name: 'point_record',
        component: () =>
          import(/* webpackChunkName: "record" */ '@/views/PointRecord.vue'),
      },
      {
        path: 'course_data_analysis',
        name: 'course_data_analysis',
        component: () =>
          import(/* webpackChunkName: "courseDataAnalysis" */ '@/views/CourseDataAnalysis.vue'),
      },
      {
        path: 'course_record',
        name: 'course_record',
        component: () =>
          import(/* webpackChunkName: "courseRecord" */ '@/views/CourseRecord.vue'),
      },
      {
        path: 'customer_report',
        name: 'customer_report',
        component: () =>
          import(/* webpackChunkName: "customerReport" */ '@/views/CustomerReport.vue'),
      },
      {
        path: 'helpdesk',
        name: 'helpdesk',
        component: () =>
          import(/* webpackChunkName: "helpdesk" */ '@/views/HelpDesk.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

const lang = store.getters['app/locale']
const isPageExist = (to: any): boolean => {
  const resolvedRoute = router.resolve({ name: to.name, params: to.params })
  if (to.name === undefined || resolvedRoute.matched.length === 0) {
    return false
  }
  return true
}

const tryRefreshMember = async (): Promise<boolean> => {
  if (await store.dispatch('user/getAccessTokenFromCookie')) {
    try {
      await store.dispatch('user/refresh')
      return true
    } catch {
      // do nothing and return false in last line
    }
  }

  return false
}

router.beforeEach(async (to: any, from: any, next: any) => {
  if (to.path.match(/register/) ||
    to.path.match(/confirm-email/)) {
    next()
  } 
  else if (to.path.match(/forget-password/) || to.path.match(/reset-password/)) {
    const userData = store.getters['user/user']
    let isLogin: boolean = userData && userData.id
    if (!isLogin) {
      isLogin = await tryRefreshMember()
    }
    isLogin ? next(`/${lang}/member_check`) : next()
  }
  else if (store.getters['user/user']?.id || await tryRefreshMember()) {

    if (from.query?.sid) {
      store.commit('routeRecord/setSsoRedirectTo', { ...from.query })
    }

    if (to.path.match(/login/)) {
      next('/' + lang + '/member_check')
    } else {
      const permission: boolean = isPageExist(to)
      if (permission) {
        next()
      } else {
        store.commit('routeRecord/setShowNoPermissionErrorFlag', true)
        store.commit('routeRecord/setUrlTo', null)
        next('/' + lang + '/member_check')
      }
    }
  } else if (to.path.match(/login/)) {
    next()
  } else {
    store.commit('routeRecord/setShowNoPermissionErrorFlag', true)
    store.commit('routeRecord/setUrlTo', null)
    next('/' + lang + '/login')
  }
})


export default router
